import React, { useMemo, useRef } from "react";
import { getSymbolById } from "../../FloorPlanModal/utils/symbolTypes";
import { getStationColor } from "../../FloorPlanModal/utils/stationUtils";
import { screenRotationDeg } from "../../FloorPlanModal/utils/viewport";
import { buildCapsulePath } from "../utils/capsulePath";
import EquipmentPopover from "./EquipmentPopover";

// How far (METRES of real ground) a machine may move between two 2s polls
// before we treat it as a teleport (e.g. re-acquisition after being offline)
// instead of real motion. Below this, the CSS transition glides; at/above it
// we snap so the icon doesn't visibly slide across the whole canvas.
//
// This used to be a 120px screen-distance threshold. Screen distance is the
// wrong unit for the question being asked ("did this machine plausibly move
// there in 2s?"): it silently changed meaning with the canvas size, and once
// the viewport can zoom it would change meaning with the zoom level too --
// zooming in far enough would push every ordinary 2s step past 120px and make
// every machine snap permanently. 20m is what 120px worked out to at a
// typical plane-site scale (~6 px/m: a few-hundred-metre site in a
// ~1000px-wide canvas), and it is also a sane physical bound in its own
// right -- 20m in 2s is 36 km/h, far above anything that moves on site.
const LARGE_JUMP_SNAP_METERS = 20;
const POSITION_TRANSITION = "transform 0.5s ease-out";

// Machine body line thickness (px).
const BODY_STROKE_PX = 2.5;

// The zone is a closed <path> (a capsule — see buildCapsulePath), so unlike
// a stroked line it has a real boundary to draw on. That means it can take
// the EXACT SAME fill/stroke/strokeWidth props as 防護區's <circle>
// (LiveCanvas.jsx renderProtectionZones) rather than an approximation of
// them — the literal is copied from there on purpose, keep them in sync.
const ZONE_FILL = "rgba(250, 140, 22, 0.2)";
const ZONE_BORDER_WIDTH = 1;
// Degraded machines get the grey palette already used for the true-degraded
// dot below, plus a dashed border. The fill stays solid/undashed so the
// shape still reads as an area rather than a set of stripes.
const ZONE_FILL_DEGRADED = "rgba(140, 140, 140, 0.2)";
const ZONE_BORDER_DEGRADED = "#8c8c8c";
const ZONE_BORDER_DASH_DEGRADED = "6 6";
// The degraded machine's icon wears this too, so the marker and its zone are
// visibly the same signal rather than two separate greys.
const DEGRADED_MARKER_COLOR = ZONE_BORDER_DEGRADED;

// A CSS border-triangle points UP, i.e. its intrinsic direction is 90deg CCW
// in the metres frame. headingDeg is measured CCW from +x, so the arrow turns
// by (headingDeg - 90) CCW to point ALONG the machine's heading instead of
// across it; screenRotationDeg converts that to a CSS (clockwise) rotate().
// headingDeg 90 (north) therefore renders rotate(0deg).
const TRIANGLE_UP_OFFSET_DEG = 90;

// The backend's `pose.degraded` flag alone can't tell "a two-tag machine
// that lost one tag" (a real fault) apart from "a one-tag machine working
// exactly as configured" (compute_pose reports degraded=True for any single
// fresh point, regardless of how many tags exist). Derive the real
// three-way state from the tags array itself, which already carries both
// how many tags are CONFIGURED and how many are currently FRESH:
//   - configured === 1                    -> healthy single-point machine
//   - configured >= 2 && fresh === 1      -> true degradation (lost a tag)
//   - configured >= 2 && fresh >= 2       -> full body (head+tail/polygon)
const getEquipmentFaultState = (item) => {
  const tags = item.tags || [];
  const configuredCount = tags.length;
  const freshCount = tags.filter((tag) => tag.fresh).length;
  return {
    isHealthySinglePoint: configuredCount === 1,
    isTrueDegraded: configuredCount >= 2 && freshCount === 1,
  };
};

// --- Alert-zone geometry, drawn by LiveCanvas inside its EXISTING <svg> ---
// overlay (the same one that already draws 防護區 circles), so it shares
// the same viewport and paints in the same coordinate space. The zone is the
// capsule (buffer of radius `alertRadiusMeters*viewport.pxPerMeter` around the
// head<->tail segment, or a circle around the single remaining point) that
// the backend actually alarms on (`distance_to_body`) — not a circle around
// the centroid, which would visually lie about the trigger area.
// plane-only: line/tunnel layouts render plain markers with no rotated body,
// so there is no oriented "body line" to buffer there.
export const renderEquipmentZones = ({ equipment, layoutType, viewport }) => {
  if (!equipment?.length || !viewport?.isValid || layoutType !== "plane") {
    return null;
  }

  const protectionZoneBorder = getStationColor("防護區").border;

  return equipment
    .filter((item) => item.status !== "offline" && item.pose)
    .map((item) => {
      const { pose, alertRadiusMeters, id } = item;
      if (!alertRadiusMeters || !pose.bodyPoints?.length) return null;

      const { isTrueDegraded } = getEquipmentFaultState(item);

      const points = pose.bodyPoints.map(viewport.project);
      // 1 point (a healthy single-tag machine, or a degraded two-tag one) or
      // 2 points (head/tail). With one point, buildCapsulePath degenerates
      // to a plain circle — the correct zone shape for a single-point
      // machine either way, only the colour/dash differ by fault state
      // below.
      const [p1, p2 = p1] = points;
      const radius = alertRadiusMeters * viewport.pxPerMeter;
      const d = buildCapsulePath(p1, p2, radius);

      return (
        <path
          key={`equipment-zone-${id}`}
          d={d}
          fill={isTrueDegraded ? ZONE_FILL_DEGRADED : ZONE_FILL}
          stroke={isTrueDegraded ? ZONE_BORDER_DEGRADED : protectionZoneBorder}
          strokeWidth={ZONE_BORDER_WIDTH}
          strokeDasharray={
            isTrueDegraded ? ZONE_BORDER_DASH_DEGRADED : undefined
          }
        />
      );
    });
};

// --- Machine body (機身) ---------------------------------------------------
// The spine that makes two markers read as ONE machine: a segment from 尾 to
// 頭 for a head/tail pair, or nothing when only one tag is fresh. Rendered by
// LiveCanvas AFTER the zones so it always sits on top of every zone fill, and
// still beneath the head/tail markers, which are HTML above the svg.
export const renderEquipmentBodies = ({ equipment, layoutType, viewport }) => {
  if (!equipment?.length || !viewport?.isValid || layoutType !== "plane") {
    return null;
  }

  return equipment
    .filter((item) => item.status !== "offline" && item.pose)
    .map((item) => {
      const { pose, id, symbolType } = item;
      const { isTrueDegraded } = getEquipmentFaultState(item);
      // A degraded machine has no trustworthy body to draw — the dashed zone
      // and the bare dot are the honest rendering.
      if (isTrueDegraded || !pose.bodyPoints?.length) return null;

      const points = pose.bodyPoints.map(viewport.project);
      // Only one fresh tag -> no second point to draw a spine to; nothing to
      // render here (the bare icon/dot already carries the machine).
      if (points.length < 2) return null;

      const color = getSymbolById(symbolType)?.color || "#722ed1";
      // pose.body_points is (head, tail) — see pose.py — so points[0] is
      // HEAD and points[1] is TAIL. A symmetric line doesn't care which end
      // is x1/x2, but naming it backwards here is exactly the kind of thing
      // the next reader would copy into somewhere that DOES care.
      const [headPoint, tailPoint] = points;
      return (
        <line
          key={`equipment-body-${id}`}
          x1={tailPoint.x}
          y1={tailPoint.y}
          x2={headPoint.x}
          y2={headPoint.y}
          stroke={color}
          strokeWidth={BODY_STROKE_PX}
          strokeLinecap="round"
        />
      );
    });
};

// Tracks the previous METRE position for one machine so we can decide,
// synchronously during render, whether this update should glide (CSS
// transition) or snap (no transition). Metres, not pixels: the question is
// about ground truth, and a metre threshold is invariant under canvas resize
// and zoom (see LARGE_JUMP_SNAP_METERS). This deliberately diverges from
// PersonnelMarker, which teleports every poll today — do not retrofit
// personnel with this in this task.
const usePositionTransition = (x, y) => {
  const prevRef = useRef(null);
  const prev = prevRef.current;
  let transitionEnabled = true;
  if (!prev) {
    transitionEnabled = false;
  } else {
    const distance = Math.hypot(x - prev.x, y - prev.y);
    if (distance >= LARGE_JUMP_SNAP_METERS) transitionEnabled = false;
  }
  prevRef.current = { x, y };
  return transitionEnabled;
};

const EquipmentMarker = ({
  item,
  viewport,
  openEquipmentId,
  setOpenEquipmentId,
}) => {
  const { pose, symbolType, status } = item;
  // The transition decision is made in METRES, before projection, so it is
  // unaffected by the canvas size or (later) the zoom level.
  const centroidMeters = pose?.centroid;
  const transitionEnabled = usePositionTransition(
    centroidMeters?.x ?? 0,
    centroidMeters?.y ?? 0,
  );

  if (!centroidMeters) return null;

  const centroid = viewport.project(centroidMeters);

  const symbol = getSymbolById(symbolType);
  const Icon = symbol?.icon;
  const color = symbol?.color || "#722ed1";
  const opacity = status === "stale" ? 0.75 : 1;

  // `item.tags` still feeds the configured-vs-fresh counts here — it is no
  // longer a source of marker GEOMETRY (see headPoint/tailPoint below).
  const { isHealthySinglePoint, isTrueDegraded } = getEquipmentFaultState(item);
  // Head/tail marks need TWO trustworthy points. A single-tag machine hasn't
  // got a second one — that is its normal, honest state, not a fault — and a
  // degraded machine can no longer trust the one it has lost. Both therefore
  // render the bare icon; only the colour/border differ between them.
  const showHeadTail = !isTrueDegraded && !isHealthySinglePoint;
  const isOpen = openEquipmentId === item.id;

  // Markers ride the POSE, not the raw per-tag reading: pose.bodyPoints is
  // the EMA-smoothed AND span-corrected pair, while tag.positionMeters is
  // the raw measurement — span correction alone forces the head-tail
  // distance to the configured 機具長度, so drawing markers from the raw
  // tags visibly overshoots the body line/zone drawn from the pose.
  // ORDERING IS LOAD-BEARING: pose.body_points is (head, tail) — see
  // pose.py — so bodyPoints[0] is HEAD and [1] is TAIL.
  const [headPoint, tailPoint] = (pose?.bodyPoints || []).map(
    viewport.project,
  );

  // Machines are click-to-open-popover, not click-to-focus (ADDENDUM F1):
  // the trigger toggles the shared open-id directly, and the Popover's own
  // onOpenChange clears it when the user clicks elsewhere.
  const handleTriggerClick = (e) => {
    e.stopPropagation();
    setOpenEquipmentId(isOpen ? null : item.id);
  };
  const handleOpenChange = (open) => setOpenEquipmentId(open ? item.id : null);

  // A degraded machine keeps its icon but wears the same grey the dashed
  // zone uses, so the two read as one "position uncertain" signal instead of
  // two unrelated treatments.
  const markerColor = isTrueDegraded ? DEGRADED_MARKER_COLOR : color;

  // viewport.project returns the TRUE pixel position (see utils/viewport.js),
  // and the SVG zone/body renderers above project pose.bodyPoints with no
  // adjustment, so this container needs none either to land on the same
  // point every child (icon, head, tail) is positioned relative to.
  const markerLeft = centroid.x;
  const markerTop = centroid.y;

  return (
    <div
      style={{
        position: "absolute",
        left: markerLeft,
        top: markerTop,
        pointerEvents: "none",
      }}
    >
      {/* Machine body: icon at the centroid, always upright. Heading is
          already communicated by the head triangle, the tail square and
          the body line below — rotating the icon on top of that is
          redundant, and at some headings it renders the pictogram
          upside down, so it never rotates (see ADDENDUM D2).

          A degraded machine (2+ tags configured, only one still fresh)
          keeps this SAME icon rather than collapsing to an anonymous dot:
          "which machine is broken" is the first thing an operator needs,
          and a bare dot threw that away — every degraded machine looked
          identical. It is recoloured to the degraded grey and its border
          is dashed, echoing the dashed zone, so the fault stays obvious
          while the machine stays identifiable. What it still does NOT get
          is a heading, a body line or head/tail marks (see showHeadTail):
          those would be inventing a direction the data can't support. */}
      <EquipmentPopover
        item={item}
        isFaulty={isTrueDegraded}
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
      >
        <div
          onClick={handleTriggerClick}
          style={{
            position: "absolute",
            transform: "translate(-50%, -50%)",
            transition: transitionEnabled ? POSITION_TRANSITION : "none",
            opacity,
            pointerEvents: "auto",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            borderRadius: "4px",
            border: `2px ${isTrueDegraded ? "dashed" : "solid"} ${markerColor}`,
            boxShadow: `0 2px 6px ${markerColor}66`,
          }}
        >
          {Icon && <Icon size={18} style={{ color: markerColor }} />}
        </div>
      </EquipmentPopover>

      {!isTrueDegraded && (
        <>
          {/* Head: filled triangle pointing along the heading. Only a
              multi-tag machine with 2+ fresh points has a head/tail to show.
              Positioned from pose.bodyPoints (see headPoint above), NOT
              the raw tag reading, so it lands exactly on the body line's end. */}
          {showHeadTail && headPoint && (
            <div
              style={{
                position: "absolute",
                left: headPoint.x - centroid.x,
                top: headPoint.y - centroid.y,
                transform: `translate(-50%, -50%) rotate(${screenRotationDeg((pose.headingDeg ?? 0) - TRIANGLE_UP_OFFSET_DEG)}deg)`,
                transition: transitionEnabled ? POSITION_TRANSITION : "none",
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  width: 0,
                  height: 0,
                  borderLeft: "6px solid transparent",
                  borderRight: "6px solid transparent",
                  borderBottom: `11px solid ${color}`,
                }}
              />
            </div>
          )}

          {/* Tail: solid block, unmistakably different in SHAPE from the head
              arrow (not just colour), so it survives a colour-blind operator
              and a sunlit screen. A bar lying along the heading read as a
              continuation of the body line, so it is a square instead.
              Positioned from pose.bodyPoints (see tailPoint above), NOT
              the raw tag reading — same reasoning as the head marker. */}
          {showHeadTail && tailPoint && (
            <div
              style={{
                position: "absolute",
                left: tailPoint.x - centroid.x,
                top: tailPoint.y - centroid.y,
                transform: `translate(-50%, -50%) rotate(${screenRotationDeg(pose.headingDeg ?? 0)}deg)`,
                transition: transitionEnabled ? POSITION_TRANSITION : "none",
                pointerEvents: "none",
              }}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 1,
                  backgroundColor: color,
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// line/tunnel layouts: plain per-tag markers using the same x-clamp/lane
// maths as LiveCanvas's renderProtectionZones. No rotated body — an oriented
// heading/body only makes sense on the 2D plane layout.
const EquipmentLaneMarkers = ({ equipment, viewport }) => {
  return equipment
    .filter((item) => item.status !== "offline")
    .flatMap((item) =>
      (item.tags || [])
        .filter((tag) => tag.positionMeters)
        .map((tag) => {
          const { x, y } = viewport.place(tag.positionMeters);

          const symbol = getSymbolById(item.symbolType);
          const color = symbol?.color || "#722ed1";

          return (
            <div
              key={`equipment-lane-${item.id}-${tag.id}`}
              style={{
                position: "absolute",
                left: x,
                top: y,
                transform: "translate(-50%, -50%)",
                pointerEvents: "none",
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: color,
                border: "1px solid #ffffff",
                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
              }}
              title={`${item.name}－${tag.name}`}
            />
          );
        }),
    );
};

const EquipmentLayer = ({
  equipment,
  layoutType,
  viewport,
  visible,
  openEquipmentId,
  setOpenEquipmentId,
}) => {
  const visibleEquipment = useMemo(
    () =>
      visible ? (equipment || []).filter((e) => e.status !== "offline") : [],
    [equipment, visible],
  );

  if (visibleEquipment.length === 0) return null;

  if (layoutType !== "plane") {
    return (
      <EquipmentLaneMarkers equipment={visibleEquipment} viewport={viewport} />
    );
  }

  return visibleEquipment.map((item) => (
    <EquipmentMarker
      key={item.id}
      item={item}
      viewport={viewport}
      openEquipmentId={openEquipmentId}
      setOpenEquipmentId={setOpenEquipmentId}
    />
  ));
};

export default EquipmentLayer;
